import { Box } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { AppNoFeedback } from '../components/app/appNoFeedback';
import { Client } from '../components/clients/client';
import { ClientDialogAdd } from '../components/clients/clientDialogAdd';
import { ClientDialogEdit } from '../components/clients/clientDialogEdit';
import { ToolboxClients } from '../components/clients/clientsToolbox';
import { AppMain } from '../layout/appMain';
import { Client as ClientModel } from '../models/client';
import { GetClientParams } from '../services/api/clientServices';
import AddIcon from '@mui/icons-material/Add';
import AppLoading from '../components/app/appLoading';
import { useGetClients } from '../hooks/useGetClients';

const style = {
  box: {
    mt: 4,
    display: 'flex',
    flexWrap: 'wrap',
  },
} as const;

export function PageClients(): JSX.Element {
  const [isOpenAddClient, setIsOpenAddClient] = useState<boolean>(false);
  const [isOpenEditClient, setIsOpenEditClient] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useState<GetClientParams>({});
  const [clientToEdit, setClientToEdit] = useState<ClientModel | null>(null);

  const { clients, isLoading, refetch } = useGetClients();

  const handleRequestSuccessful = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleSearchClients = useCallback(
    (params: GetClientParams) => {
      setSearchParams(params);
      refetch(params);
    },
    [refetch]
  );

  const handleReloadClients = useCallback(
    () => {
      refetch(searchParams);
    },
    [searchParams, refetch]
  );

  const handleCloseAddDialogClient = useCallback(() => {
    setIsOpenAddClient(false);
  }, []);

  const handleOpenAddDialogClient = useCallback(() => {
    setIsOpenAddClient(true);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setIsOpenEditClient(false);
  }, []);

  const handleEditClient = useCallback((client: ClientModel) => {
    setIsOpenEditClient(true);
    setClientToEdit(client);
  }, []);

  const headerButtons = useMemo(() => {
    return [{ icon: AddIcon, onClick: handleOpenAddDialogClient }];
  }, [handleOpenAddDialogClient]);

  return (
    <AppMain buttons={headerButtons}>
      <ClientDialogAdd
        isOpen={isOpenAddClient}
        onCloseAddDialog={handleCloseAddDialogClient}
        onRequestSuccessful={handleRequestSuccessful}
      />

      <ClientDialogEdit
        isOpen={isOpenEditClient}
        onCloseEditDialog={handleCloseEditDialog}
        client={clientToEdit}
        onRequestSuccessful={handleReloadClients}
      />
      <ToolboxClients
        onSearch={handleSearchClients}
        onOpenAddDialog={handleOpenAddDialogClient}
      />

      {isLoading ? (
        <AppLoading />
      ) : (
        <Box sx={{ ...style.box }}>
          {clients.length == 0 && <AppNoFeedback info="client" />}
          {clients.length != 0 &&
            clients.map((client) => (
              <Client
                client={client}
                handleEditClient={handleEditClient}
                key={client.id}
                onRequestSuccessful={handleRequestSuccessful}
              />
            ))}
        </Box>
      )}
    </AppMain>
  );
}
